// This file configures the initialization of Sentry and PostHog on the client.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRewritesTunnelPath"] = "/monitoring";globalThis["SENTRY_RELEASE"] = {"id":"c89874e2e24230338867631a43a953ffc2dc163f"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryNextJsVersion"] = "15.5.18";globalThis["_sentryRewriteFramesAssetPrefixPath"] = "";globalThis["_sentryAssetPrefix"] = undefined;globalThis["_sentryExperimentalThirdPartyOriginStackFrames"] = undefined;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/lessons/:lessonId/practice\",\"regex\":\"^/lessons/([^/]+)/practice$\",\"paramNames\":[\"lessonId\"],\"hasOptionalPrefix\":false},{\"path\":\"/subjects/:id/mocks/:paperId\",\"regex\":\"^/subjects/([^/]+)/mocks/([^/]+)$\",\"paramNames\":[\"id\",\"paperId\"],\"hasOptionalPrefix\":false},{\"path\":\"/subjects/:id/papers/:paperId\",\"regex\":\"^/subjects/([^/]+)/papers/([^/]+)$\",\"paramNames\":[\"id\",\"paperId\"],\"hasOptionalPrefix\":false},{\"path\":\"/open/:url*\",\"regex\":\"^/open/(.+)$\",\"paramNames\":[\"url\"],\"hasOptionalPrefix\":false},{\"path\":\"/open/collection/:collectionId\",\"regex\":\"^/open/collection/([^/]+)$\",\"paramNames\":[\"collectionId\"],\"hasOptionalPrefix\":false},{\"path\":\"/open/collection/:collectionId/doc/:documentId\",\"regex\":\"^/open/collection/([^/]+)/doc/([^/]+)$\",\"paramNames\":[\"collectionId\",\"documentId\"],\"hasOptionalPrefix\":false},{\"path\":\"/open/doc/:documentId\",\"regex\":\"^/open/doc/([^/]+)$\",\"paramNames\":[\"documentId\"],\"hasOptionalPrefix\":false},{\"path\":\"/invite/:token\",\"regex\":\"^/invite/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false},{\"path\":\"/referral/:code\",\"regex\":\"^/referral/([^/]+)$\",\"paramNames\":[\"code\"],\"hasOptionalPrefix\":false},{\"path\":\"/share/:token\",\"regex\":\"^/share/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false},{\"path\":\"/share/module/:token\",\"regex\":\"^/share/module/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/onboarding\"},{\"path\":\"/open\"},{\"path\":\"/open/home\"},{\"path\":\"/open/docbench\"},{\"path\":\"/open/email-preview\"},{\"path\":\"/open/onboarding\"},{\"path\":\"/open/prompt-viewer\"},{\"path\":\"/open/storybook\"},{\"path\":\"/plan\"},{\"path\":\"/thankyou\"},{\"path\":\"/auth/impersonate\"},{\"path\":\"/auth/login\"},{\"path\":\"/auth/reset-password\"},{\"path\":\"/auth/signup\"},{\"path\":\"/matcha\"},{\"path\":\"/matcha/login\"},{\"path\":\"/picker\"}],\"isrRoutes\":[]}";import {
  CLIENT_IGNORE_SPANS,
  CLIENT_TRANSPORT_OPTIONS,
  enableClientLogs,
} from "@medly/sentry-config";
import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";
import {
  replaysOnErrorSampleRate,
  replaysSessionSampleRate,
  sharedSentryConfig,
} from "../sentry.shared.config";

posthog.init(process.env.NEXT_PUBLIC_POSTHOG_ID!, {
  api_host: "/t",
  ui_host: "https://eu.posthog.com",
  person_profiles: "identified_only",
  // Cost controls — see PR #1113 for the wider PostHog spend audit. We
  // already capture meaningful clicks explicitly, Sentry handles errors,
  // and `$feature_flag_called` is a debug-only event that the polling in
  // usePostHogFlag fires on every render.
  autocapture: false,
  capture_exceptions: false,
  before_send: (event) => {
    if (!event) return null;
    if (event.event === "$feature_flag_called") return null;
    return event;
  },
});
(window as { posthog?: typeof posthog }).posthog = posthog;

Sentry.init({
  ...sharedSentryConfig,
  enableLogs: enableClientLogs,
  replaysSessionSampleRate,
  replaysOnErrorSampleRate,
  // Send envelopes without cookies — the same-origin /monitoring tunnel
  // otherwise forwards the whole cookie jar to Sentry ingest, which rejects
  // oversized headers with 400 and drops the telemetry.
  transportOptions: CLIENT_TRANSPORT_OPTIONS,
  // Drop resource spans for third-party pixel/analytics assets (their
  // fetch/XHR requests are already excluded via shouldCreateSpanForRequest).
  ignoreSpans: CLIENT_IGNORE_SPANS,
  integrations: [
    Sentry.consoleLoggingIntegration(),
    Sentry.browserTracingIntegration({
      // Only create performance spans for our own services.
      // Third-party scripts (TikTok, Facebook, PostHog, etc.) still appear
      // as breadcrumbs on error events, but won't generate traced spans.
      shouldCreateSpanForRequest: (url) => {
        if (url.startsWith("/t/")) return false;
        if (url.startsWith("/monitoring")) return false;
        return (
          url.startsWith("/") ||
          url.includes("medlyai.com") ||
          url.includes("medly-db-api") ||
          url.includes("mathpix.com")
        );
      },
    }),
    Sentry.featureFlagsIntegration(),
    Sentry.feedbackIntegration({
      colorScheme: "system",
      autoInject: false,
      useSentryUser: {
        email: "email",
        name: "username",
      },
      // Flush the rolling replay buffer so the submitted replay includes the
      // ~60s before the click. Fall back to start() if nothing is buffered
      // yet (e.g. form opened in the first tick after load) so bug reports
      // still have a replay attached.
      onFormOpen: () => {
        const replay = Sentry.getReplay();
        replay?.flush().catch(() => replay?.start());
      },
    }),
    // Session Replay — attaches to user-submitted "Report a bug" feedback
    // so we can see what the user did. Text and inputs are shown unmasked:
    // the captured content (chat UI, canvas prompts) is not sensitive, and
    // unmasking makes bug reports actually readable.
    Sentry.replayIntegration({
      blockAllMedia: false,
      maskAllText: false,
      maskAllInputs: false,
      // startBuffering() below runs a global rolling buffer so "Report a
      // bug" can flush the preceding window. Disable the SDK's auto-flush
      // on captured errors entirely — bug reports are our only replay
      // channel. See sentry.shared.config.ts for the matching session/
      // on-error sample rates of 0.
      beforeErrorSampling: () => false,
    }),
  ],
  // Propagate traces to same-origin and the curriculum API backend
  tracePropagationTargets: [
    "localhost",
    // Same-origin relative URLs, excluding PostHog proxy (/t/) and Sentry tunnel (/monitoring)
    /^\/(?!t\/|monitoring)/,
    new RegExp(`^${process.env.NEXT_PUBLIC_CURRICULUM_API_URL}`),
  ],
});

// Keep a rolling replay buffer running so "Report a bug" can flush the
// window preceding the click (~60s by default). No-op when the session is
// already sampled into session-mode replay via replaysSessionSampleRate.
Sentry.getReplay()?.startBuffering();

// =============================================================================
// POSTHOG FEATURE FLAGS SYNC
// =============================================================================

type PostHogInstance = {
  onFeatureFlags: (
    callback: (
      flags: string[],
      variants: Record<string, string | boolean>,
    ) => void,
  ) => void;
};

function syncPostHogFlagsToSentry() {
  if (typeof window === "undefined") return;

  const trySync = () => {
    const posthog = (window as { posthog?: PostHogInstance }).posthog;
    if (!posthog?.onFeatureFlags) return false;

    posthog.onFeatureFlags((flags, variants) => {
      const integration =
        Sentry.getClient()?.getIntegrationByName<Sentry.FeatureFlagsIntegration>(
          "FeatureFlags",
        );
      if (!integration) return;

      for (const flag of flags) {
        const value = variants[flag];
        integration.addFeatureFlag(
          flag,
          typeof value === "boolean" ? value : value !== "false" && !!value,
        );
      }
    });
    return true;
  };

  // Try immediately, then poll for up to 5 seconds
  if (trySync()) return;

  let attempts = 0;
  const interval = setInterval(() => {
    if (trySync() || ++attempts >= 50) {
      clearInterval(interval);
    }
  }, 100);
}

syncPostHogFlagsToSentry();

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
